import { useCallback, useEffect, useRef, useState, type CSSProperties, type TouchEvent } from "react";
import { ArrowRight, Check, ChevronLeft, ChevronRight, CircleAlert } from "lucide-react";
import { MirrorAtmosphere } from "@/components/MirrorAtmosphere";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface TutorialProps {
  onComplete: () => void;
}

const SLIDES = [
  {
    label: "THE PREMISE",
    title: "A place to hear yourself think.",
    copy: "Fractal turns ordinary conversation into a record of your patterns. You talk. It notices.",
    aside: "Conversation becomes a second kind of memory.",
  },
  {
    label: "YOUR PART",
    title: "Bring the unedited version.",
    copy: "Tell it what's going on, the way you'd tell a friend. No forms, no blank page. It pulls out the details on its own.",
    aside: "A thought does not need to arrive finished.",
  },
  {
    label: "WITH TIME",
    title: "Small moments start to connect.",
    copy: "After a few days you'll see entries, then daily recaps. After about a week, patterns — the connections you can't see from inside your own life.",
    aside: "Entries → recaps → patterns",
  },
  {
    label: "THE BOUNDARY",
    title: "A mirror, not an authority.",
    copy: "Not a therapist, not a coach. It won't tell you what to do. It shows you what's actually there and leaves the decision to you.",
    aside: "You remain the person in charge.",
  },
] as const;

const EASE = "cubic-bezier(.16,1,.3,1)";

function OrbitGraphic({ step }: { step: number }) {
  return (
    <div className={`tutorial-art tutorial-art-${step}`} aria-hidden="true">
      <div className="art-orbit art-orbit-one" />
      <div className="art-orbit art-orbit-two" />
      <div className="art-core">
        {step === 0 && <span className="art-eye" />}
        {step === 1 && <span className="art-pencil" />}
        {step === 2 && <span className="art-constellation"><i /><i /><i /></span>}
        {step === 3 && <Check size={32} strokeWidth={1.2} />}
      </div>
      <span className="art-caption">{step === 0 ? "NOTICE" : step === 1 ? "SAY IT" : step === 2 ? "SEE IT" : "YOUR CALL"}</span>
    </div>
  );
}

export default function Tutorial({ onComplete }: TutorialProps) {
  const [step, setStep] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchDelta, setTouchDelta] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(false);
  const touchDeltaRef = useRef(0);

  const handleComplete = useCallback(async () => {
    if (completing) return;
    setCompleting(true);
    setError(false);
    try {
      await apiRequest("POST", "/api/auth/tutorial-complete");
      setExiting(true);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      window.setTimeout(() => onComplete(), 400);
    } catch {
      setCompleting(false);
      setError(true);
    }
  }, [completing, onComplete]);

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= SLIDES.length || index === step || completing) return;
    setStep(index);
  }, [completing, step]);

  const next = useCallback(() => {
    if (step === SLIDES.length - 1) handleComplete();
    else goTo(step + 1);
  }, [goTo, handleComplete, step]);

  const prev = useCallback(() => goTo(step - 1), [goTo, step]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        next();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        prev();
      } else if (event.key === "Escape") {
        event.preventDefault();
        handleComplete();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleComplete, next, prev]);

  const handleTouchStart = (event: TouchEvent) => {
    setTouchStart(event.touches[0].clientX);
    touchDeltaRef.current = 0;
  };
  const handleTouchMove = (event: TouchEvent) => {
    if (touchStart === null) return;
    const delta = event.touches[0].clientX - touchStart;
    touchDeltaRef.current = delta;
    setTouchDelta(delta);
  };
  const handleTouchEnd = () => {
    const delta = touchDeltaRef.current;
    if (Math.abs(delta) > 56) delta < 0 ? next() : prev();
    setTouchStart(null);
    touchDeltaRef.current = 0;
    setTouchDelta(0);
  };

  const current = SLIDES[step];
  const trackStyle = {
    transform: `translateX(calc(${step * -100}% + ${touchStart !== null ? touchDelta : 0}px))`,
  };

  return (
    <div
      className={`tutorial-room ${exiting ? "is-exiting" : ""}`}
      data-testid="tutorial-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Fractal orientation"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ "--tutorial-accent": "var(--accent-color)" } as CSSProperties}
    >
      <MirrorAtmosphere immersive opacityScale={0.18} count={2400} />
      <div className="tutorial-wash" aria-hidden="true" />
      <header className="tutorial-header">
        <div className="tutorial-brand"><span className="brand-mark">FRACTAL</span><span className="brand-status">· LIVE</span></div>
        <div className="tutorial-context">A SHORT ORIENTATION <span>·</span> {step + 1} OF {SLIDES.length}</div>
        <button className="tutorial-skip" onClick={handleComplete} disabled={completing} data-testid="button-skip-tutorial">Leave orientation</button>
      </header>

      <div className="tutorial-progress" aria-label="Tutorial progress">
        {SLIDES.map((slide, index) => (
          <button
            key={slide.label}
            className={`progress-step ${index === step ? "is-active" : ""} ${index < step ? "is-past" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Go to ${slide.label.toLowerCase()}`}
            aria-current={index === step ? "step" : undefined}
            data-testid={`tutorial-dot-${index}`}
          >
            <span>{String(index + 1).padStart(2, "0")}</span>
            <i />
          </button>
        ))}
      </div>

      <main className="tutorial-stage" aria-live="polite">
        <div className={`tutorial-track ${touchStart !== null ? "is-dragging" : ""}`} style={trackStyle}>
          {SLIDES.map((slide, index) => (
            <section className="tutorial-slide" key={slide.label} aria-hidden={index !== step} data-testid={`tutorial-card-${index}`}>
              <div className="tutorial-copy">
                <div className="slide-kicker"><span /> {slide.label}</div>
                <h1>{slide.title}</h1>
                <p>{slide.copy}</p>
                <div className="slide-aside">{slide.aside}</div>
              </div>
              <div data-testid={`tutorial-animation-${index}`}><OrbitGraphic step={index} /></div>
            </section>
          ))}
        </div>
      </main>

      <footer className="tutorial-footer">
        <div className="tutorial-hint">Use the arrows <span aria-hidden="true">·</span> swipe to move <span aria-hidden="true">·</span> esc to leave</div>
        <div className="tutorial-controls">
          <button className="tutorial-arrow" onClick={prev} disabled={step === 0 || completing} aria-label="Previous slide" data-testid="button-tutorial-prev"><ChevronLeft size={19} /></button>
          <button className="tutorial-next" onClick={next} disabled={completing} data-testid="button-tutorial-next">
            {completing ? "Saving..." : step === SLIDES.length - 1 ? "Enter Fractal" : "Continue"} <ArrowRight size={16} />
          </button>
          <button className="tutorial-arrow" onClick={next} disabled={step === SLIDES.length - 1 || completing} aria-label="Next slide" data-testid="button-tutorial-next-arrow"><ChevronRight size={19} /></button>
        </div>
        {error && (
          <div className="tutorial-error" role="alert">
            <CircleAlert size={15} /> We couldn't save that yet.
            <button onClick={handleComplete}>Try again</button>
          </div>
        )}
      </footer>
    </div>
  );
}
